import { forwardRef } from 'react';
import { InputValue, Position, Sized } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { BareIconButton } from '../BareIconButton';
import { CircleLoader } from '../CircleLoader';
import { ErrorIcon } from '../common/ErrorIcon';
import { Tooltip } from '../Tooltip';
import {
    IndicativeIconWrapper,
    InputContainer,
    InputElement,
    InputWrapper,
    LeftSide,
    PlaceholderElement,
    Prefix,
    RightSide,
    Suffix,
} from './style';

const TooltippedErrorIcon = Tooltip(ErrorIcon);

export interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'placeholder' | 'onChange' | 'prefix'>,
        Sized {
    name: string;
    id?: string;
    indicativeIcon?: IconName;
    interactiveIcon?: IconName;
    interactiveIconHandler?: () => void;
    clearable?: boolean;
    value?: InputValue;
    placeholder?: React.ReactNode;
    onChange?: (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: InputValue) => void;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    loading?: boolean;
    onClear?: () => void;
    leadingImage?: React.ReactNode;
    trailingImage?: React.ReactNode;
    alignRight?: boolean;
    inline?: boolean;
    error?: React.ReactNode;
    readOnly?: boolean;
}

const Input = forwardRef(
    (
        {
            name,
            id,
            indicativeIcon,
            interactiveIcon,
            clearable,
            value,
            onChange,
            placeholder,
            interactiveIconHandler,
            size,
            suffix,
            prefix,
            disabled,
            readOnly,
            required,
            loading,
            onClear,
            leadingImage,
            trailingImage,
            alignRight,
            inline,
            error,
            ...other
        }: InputProps,
        ref: React.Ref<HTMLInputElement>
    ) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (onChange) {
                onChange(event, event.target.value);
            }
        };

        const handleClearInput = (event: React.MouseEvent) => {
            if (onClear) {
                onClear();
            } else if (onChange) {
                onChange(event, '');
            }
        };

        const hasRightSide = !!interactiveIcon || clearable || !!suffix || loading || !!trailingImage;
        const hasLeftSide = !!indicativeIcon || !!prefix || !!leadingImage;

        return (
            <InputContainer readOnly={readOnly} inputSize={size} disabled={disabled}>
                {hasLeftSide ? (
                    <LeftSide inputSize={size} spacing="50" itemAlignment="center">
                        {indicativeIcon ? (
                            <IndicativeIconWrapper
                                $disabled={disabled}
                                size="small"
                                value={indicativeIcon}
                                data-test-indicative-icon
                            />
                        ) : null}
                        {leadingImage}
                        {prefix ? (
                            <Prefix $disabled={disabled} data-test-prefix>
                                {prefix}
                            </Prefix>
                        ) : null}
                    </LeftSide>
                ) : null}

                <InputWrapper>
                    {!value && placeholder ? (
                        <PlaceholderElement
                            $readOnly={readOnly}
                            inputSize={size}
                            disabled={disabled}
                            hasRightSide={hasRightSide}
                            hasLeftSide={hasLeftSide}
                            data-test-id="ds-placeholder-element"
                        >
                            {placeholder}
                        </PlaceholderElement>
                    ) : null}
                    {/* Since types/styled-components v5 they correctly type `as` casting. For us this means that
                div doesn't have readonly and other properties, once the TODO below is done this ignore can be deleted
                @ts-expect-error */}
                    <InputElement
                        // TODO:  https://mews.myjetbrains.com/youtrack/issue/RND-33589
                        // We miss proper readOnly implementation. This is done mostly for TimePicker
                        // backward compatibility support, since it was leveraging `inputFactory` from original Input component
                        // to provide its own vision of `readonly` state
                        as={readOnly ? 'div' : undefined}
                        readOnly={readOnly}
                        name={name}
                        value={value}
                        onChange={handleChange}
                        inputSize={size}
                        disabled={disabled}
                        ref={ref}
                        alignRight={alignRight}
                        {...other}
                        id={name}
                        hasRightSide={hasRightSide}
                        hasLeftSide={hasLeftSide}
                        aria-required={required}
                        required={required}
                        aria-errormessage={`${name}-error`}
                        aria-invalid={!!error}
                    >
                        {readOnly ? value : null}
                    </InputElement>
                    {readOnly ? <input type="hidden" name={name} value={value} readOnly /> : null}
                </InputWrapper>

                {hasRightSide ? (
                    <RightSide inputSize={size} spacing="100" itemAlignment="center">
                        {suffix ? (
                            <Suffix $disabled={disabled} data-test-suffix>
                                {suffix}
                            </Suffix>
                        ) : null}
                        {clearable && value ? (
                            <BareIconButton
                                size="small"
                                icon={IconName.CrossClose}
                                onClick={handleClearInput}
                                disabled={disabled}
                                data-test-button-clear
                            />
                        ) : null}
                        {loading ? <CircleLoader size="small" data-test-loader /> : null}
                        {trailingImage}
                        {interactiveIcon ? (
                            <BareIconButton
                                size="small"
                                icon={interactiveIcon}
                                onClick={interactiveIconHandler}
                                disabled={disabled}
                                data-test-icon-interactive
                            />
                        ) : null}
                        {inline && error ? (
                            <TooltippedErrorIcon
                                size="small"
                                value={IconName.ErrorCircle}
                                tooltip={error}
                                tooltipPosition={Position.Right}
                            />
                        ) : null}
                    </RightSide>
                ) : null}
            </InputContainer>
        );
    }
);

export default Input;
